import React, { useState, useEffect } from "react";
import { useHistory, useParams } from "react-router-dom";
import {
    makeStyles,
    Typography,
    Paper,
    Grid,
    Box,
    Button,
    TextField,
    Tabs,
    Tab,
} from "@material-ui/core";
import { ToggleButton, ToggleButtonGroup } from "@material-ui/lab";
import ArrowBackIcon from "@material-ui/icons/ArrowBack";
import SaveIcon from "@material-ui/icons/Save";
import CloudUploadIcon from "@material-ui/icons/CloudUpload";
import { CKEditor } from "@ckeditor/ckeditor5-react";
import Editor from "ckeditor5-custom-build/build/ckeditor";
import editorConfiguration from "../../../util/EditorConfiguration";
import apiHelper from "../../../util/ApiHelper";
import { useLoading } from "../../../util/LoadingProvider";
import ImageUploadField from "../../common/ImageUploadField";

const couponEditorConfig = {
    ...editorConfiguration,
    site: 'cineclub',
    folder: 'PROMO',
};

const useStyles = makeStyles((theme) => ({
    content: {
        padding: theme.spacing(3),
        "& .ck-editor__editable_inline": {
            minHeight: "200px",
        },
    },
    header: {
        marginBottom: theme.spacing(3),
        display: "flex",
        alignItems: "center",
        gap: theme.spacing(2),
    },
    backButton: {
        marginRight: theme.spacing(2),
    },
    formCard: {
        padding: theme.spacing(3),
        marginBottom: theme.spacing(3),
    },
    formField: {
        marginBottom: theme.spacing(2),
    },
    buttonGroup: {
        display: "flex",
        gap: theme.spacing(2),
        marginTop: theme.spacing(3),
    },
    box: {
        padding: 0,
    },
    innerBox: {
        background: "#ffffff",
        padding: theme.spacing(3),
        marginBottom: theme.spacing(4),
    },
    uploadSection: {
        padding: theme.spacing(3),
        marginTop: theme.spacing(3),
        marginBottom: theme.spacing(2),
    },
    sectionTitle: {
        marginBottom: theme.spacing(1),
    },
    uploadDescription: {
        marginBottom: theme.spacing(2),
    },
    uploadButtonContainer: {
        display: "flex",
        justifyContent: "flex-start",
    },
    sectionText: {
        fontSize: "13px",
        fontWeight: 700,
        paddingLeft: "4px",
        marginBottom: theme.spacing(1),
    },
    statusToggle: {
        width: "50%",
        "& button": {
            fontSize: "13px",
            lineHeight: "15px",
        },
    },
    statusField: {
        marginTop: theme.spacing(3),
    },
}));

function TabPanel(props) {
    const classes = useStyles();
    const { children, value, index, ...other } = props;
    return (
        <div
            role="tabpanel"
            hidden={value !== index}
            id={`nav-tabpanel-${index}`}
            aria-labelledby={`nav-tab-${index}`}
            {...other}
        >
            <Box p={3} className={classes.box}>
                {children}
            </Box>
        </div>
    );
}

function a11yProps(index) {
    return {
        id: `nav-tab-${index}`,
        "aria-controls": `nav-tabpanel-${index}`,
    };
}

const CouponEditingPage = () => {
    const classes = useStyles();
    const history = useHistory();
    const { id } = useParams();
    const { setLoading } = useLoading();
    
    const isEditMode = !!id && id !== "create";
    const [activeTab, setActiveTab] = useState(0);

    const [formData, setFormData] = useState({
        titleEn: "",
        titleTc: "",
        descriptionEn: "",
        descriptionTc: "",
        pointPrice: "",
        orderLimit: "",
        validFrom: "",
        expiryDate: "",
        imagePath: "",
        status: true,
    });

    const [errors, setErrors] = useState({});
    const [isUploading, setIsUploading] = useState(false);

    // Load existing coupon data if in edit mode
    useEffect(() => {
        if (isEditMode) {
            loadCouponData();
        }
    }, [id]);

    const loadCouponData = async () => {
        setLoading(true);
        try {
            const response = await apiHelper.cineclub.getPromoTemplateById(id);
            if (response.data) {
                const coupon = response.data;
                setFormData({
                    titleEn: coupon.titleEn || "",
                    titleTc: coupon.titleTc || "",
                    descriptionEn: coupon.descriptionEn || "",
                    descriptionTc: coupon.descriptionTc || "",
                    pointPrice: coupon.pointPrice || "",
                    orderLimit: coupon.orderLimit != null ? String(coupon.orderLimit) : "",
                    validFrom: coupon.validFrom ? coupon.validFrom.split('T')[0] : "",
                    expiryDate: coupon.expiryDate ? coupon.expiryDate.split('T')[0] : "",
                    imagePath: coupon.imagePath || "",
                    status: coupon.status !== undefined ? coupon.status : true,
                });
            }
        } catch (error) {
            console.error("Error loading coupon data:", error);
            alert("Failed to load coupon data. Please try again.");
        } finally {
            setLoading(false);
        }
    };

    const handleInputChange = (field) => (event) => {
        const value = event.target.value;
        setFormData(prev => ({
            ...prev,
            [field]: value
        }));
        if (errors[field]) {
            setErrors(prev => ({ ...prev, [field]: "" }));
        }
    };

    const handleEditorChange = (field) => (event, editor) => {
        const data = editor.getData();
        setFormData(prev => ({ ...prev, [field]: data }));
    };

    const handleTabChange = (event, newValue) => {
        setActiveTab(newValue);
    };

    const validateForm = () => {
        const newErrors = {};
        
        if (!formData.titleEn.trim()) {
            newErrors.titleEn = "English title is required";
        }
        
        if (!formData.titleTc.trim()) {
            newErrors.titleTc = "Traditional Chinese title is required";
        }
        
        if (!formData.pointPrice || isNaN(formData.pointPrice) || parseInt(formData.pointPrice) < 0) {
            newErrors.pointPrice = "Point price must be a non-negative number";
        }
        
        if (formData.orderLimit && (isNaN(formData.orderLimit) || parseInt(formData.orderLimit) < 0 || !Number.isInteger(parseFloat(formData.orderLimit)))) {
            newErrors.orderLimit = "Order limit must be a non-negative number";
        }

        if (!formData.validFrom) {
            newErrors.validFrom = "Valid from date is required";
        }
        
        if (!formData.expiryDate) {
            newErrors.expiryDate = "Expiry date is required";
        }
        
        // Validate date range
        if (formData.validFrom && formData.expiryDate) {
            const validFromDate = new Date(formData.validFrom);
            const expiryDate = new Date(formData.expiryDate);
            if (validFromDate >= expiryDate) {
                newErrors.expiryDate = "Expiry date must be after valid from date";
            }
        }
        
        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleSubmit = async () => {
        if (!validateForm()) {
            return;
        }

        setLoading(true);
        try {
            const payload = {
                titleEn: formData.titleEn.trim(),
                titleTc: formData.titleTc.trim(),
                descriptionEn: formData.descriptionEn,
                descriptionTc: formData.descriptionTc,
                pointPrice: parseInt(formData.pointPrice),
                validFrom: formData.validFrom,
                expiryDate: formData.expiryDate,
                imagePath: formData.imagePath,
                orderLimit: formData.orderLimit ? parseInt(formData.orderLimit) : null,
                status: formData.status,
            };

            let response;
            if (isEditMode) {
                response = await apiHelper.cineclub.updatePromoTemplate(id, payload);
            } else {
                response = await apiHelper.cineclub.createPromoTemplate(payload);
            }
            
            // Log the response to debug
            console.log("API Response:", response);
            
            if (response.data && response.data.code === 0) {
                // Success - redirect back to listing page
                history.push("/cineclub/eshop/coupons");
            } else {
                // Handle error response
                const action = isEditMode ? "update" : "create";
                console.error(`Failed to ${action} coupon template:`, response);
                alert(`Failed to ${action} coupon template. Please try again.`);
            }
        } catch (error) {
            const action = isEditMode ? "updating" : "creating";
            console.error(`Error ${action} coupon template:`, error);
            alert(`An error occurred while ${action} the coupon template. Please try again.`);
        } finally {
            setLoading(false);
        }
    };

    const handleCancel = () => {
        history.push("/cineclub/eshop/coupons");
    };

    const handleImageUpload = async (field, file) => {
        if (!file) return;

        try {
            setLoading(true);
            const result = await apiHelper.cineclub.uploadMembershipFile(file, "PROMO");
            const url = result.data.url;
            // Store the full URL for display
            setFormData(prev => ({
                ...prev,
                [field]: url
            }));
        } catch (error) {
            console.error("Error uploading file:", error);
            alert("Failed to upload image. Please try again.");
        } finally {
            setLoading(false);
        }
    };

    const handleImageDelete = (field) => {
        setFormData(prev => ({
            ...prev,
            [field]: ""
        }));
    };

    const handleFileSelect = (event) => {
        const file = event.target.files[0];
        if (file) {
            // Validate file type - accept Excel files
            const validTypes = [
                'application/vnd.ms-excel',
                'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
                'text/csv'
            ];
            const validExtensions = ['.xls', '.xlsx', '.csv'];
            
            const isValidType = validTypes.includes(file.type) || 
                               validExtensions.some(ext => file.name.toLowerCase().endsWith(ext));
            
            if (!isValidType) {
                alert('Please select an Excel file (.xls, .xlsx) or CSV file.');
                return;
            }
            
            // Automatically upload the file after selection
            uploadFile(file);
        }
    };

    const uploadFile = async (file) => {
        if (!isEditMode) {
            alert('Please save the coupon template first before uploading coupons.');
            return;
        }
        
        setIsUploading(true);
        try {
            const response = await apiHelper.cineclub.importCoupons(id, file);
            
            if (response.data && response.data.code === 0) {
                alert('Coupons imported successfully!');
                // Reset file input
                const fileInput = document.getElementById('coupon-upload-input');
                if (fileInput) {
                    fileInput.value = '';
                }
                // Reload coupon data to reflect latest stock
                await loadCouponData();
            } else {
                console.error("Failed to import coupons:", response);
                alert("Failed to import coupons. Please try again.");
            }
        } catch (error) {
            console.error("Error importing coupons:", error);
            alert("An error occurred while importing coupons. Please try again.");
        } finally {
            setIsUploading(false);
        }
    };

    return (
        <div className={classes.content}>
            <Box className={classes.header}>
                <Button
                    className={classes.backButton}
                    startIcon={<ArrowBackIcon />}
                    onClick={handleCancel}
                >
                    Back to Coupons
                </Button>
                <Typography variant="h4" component="h1">
                    {isEditMode ? "Edit Coupon Template" : "Create Coupon Template"}
                </Typography>
            </Box>

            <Paper className={classes.formCard}>
                <Tabs
                    value={activeTab}
                    onChange={handleTabChange}
                    aria-label="language tabs"
                    TabIndicatorProps={{ style: { top: "0", background: "#306F9D" } }}
                >
                    <Tab label="English" {...a11yProps(0)} />
                    <Tab label="繁體中文" {...a11yProps(1)} />
                </Tabs>

                <TabPanel value={activeTab} index={0}>
                    <Box className={classes.innerBox} style={{ paddingTop: 32, marginBottom: 30 }}>
                        <Grid container spacing={3}>
                            <Grid item xs={12}>
                                <TextField
                                    fullWidth
                                    label="Title"
                                    variant="outlined"
                                    className={classes.formField}
                                    value={formData.titleEn}
                                    onChange={handleInputChange("titleEn")}
                                    error={!!errors.titleEn}
                                    helperText={errors.titleEn}
                                    required
                                />
                            </Grid>
                            <Grid item xs={12}>
                                <Typography variant="body2" className={classes.sectionText}>
                                    Description
                                </Typography>
                                <CKEditor
                                    editor={Editor}
                                    config={couponEditorConfig}
                                    data={formData.descriptionEn || ""}
                                    onChange={handleEditorChange("descriptionEn")}
                                />
                            </Grid>
                        </Grid>
                    </Box>
                </TabPanel>

                <TabPanel value={activeTab} index={1}>
                    <Box className={classes.innerBox} style={{ paddingTop: 32, marginBottom: 30 }}>
                        <Grid container spacing={3}>
                            <Grid item xs={12}>
                                <TextField
                                    fullWidth
                                    label="Title"
                                    variant="outlined"
                                    className={classes.formField}
                                    value={formData.titleTc}
                                    onChange={handleInputChange("titleTc")}
                                    error={!!errors.titleTc}
                                    helperText={errors.titleTc}
                                    required
                                />
                            </Grid>
                            <Grid item xs={12}>
                                <Typography variant="body2" className={classes.sectionText}>
                                    Description
                                </Typography>
                                <CKEditor
                                    editor={Editor}
                                    config={couponEditorConfig}
                                    data={formData.descriptionTc || ""}
                                    onChange={handleEditorChange("descriptionTc")}
                                />
                            </Grid>
                        </Grid>
                    </Box>
                </TabPanel>

                {/* Coupon Details */}
                <Box className={classes.innerBox} style={{ paddingTop: 32, marginBottom: 30 }}>
                    <Typography variant="h6" gutterBottom>
                        Coupon Template Details
                    </Typography>
                    <Grid container spacing={3}>
                        <Grid item xs={12} md={6}>
                            <TextField
                                fullWidth
                                label="Point Price"
                                type="number"
                                variant="outlined"
                                className={classes.formField}
                                value={formData.pointPrice}
                                onChange={handleInputChange("pointPrice")}
                                error={!!errors.pointPrice}
                                helperText={errors.pointPrice}
                                inputProps={{ min: 0 }}
                                required
                            />
                        </Grid>

                        <Grid item xs={12} md={6}>
                            <TextField
                                fullWidth
                                label="Order Limit"
                                type="number"
                                variant="outlined"
                                className={classes.formField}
                                value={formData.orderLimit}
                                onChange={handleInputChange("orderLimit")}
                                error={!!errors.orderLimit}
                                helperText={errors.orderLimit || "Leave empty for no limit, or enter an integer"}
                                inputProps={{ min: 0, step: 1 }}
                            />
                        </Grid>

                        <Grid item xs={12} md={6}>
                            <TextField
                                fullWidth
                                label="Valid From"
                                type="date"
                                variant="outlined"
                                className={classes.formField}
                                value={formData.validFrom}
                                onChange={handleInputChange("validFrom")}
                                InputLabelProps={{ shrink: true }}
                                error={!!errors.validFrom}
                                helperText={errors.validFrom}
                                required
                            />
                        </Grid>

                        <Grid item xs={12} md={6}>
                            <TextField
                                fullWidth
                                label="Expiry Date"
                                type="date"
                                variant="outlined"
                                className={classes.formField}
                                value={formData.expiryDate}
                                onChange={handleInputChange("expiryDate")}
                                InputLabelProps={{ shrink: true }}
                                error={!!errors.expiryDate}
                                helperText={errors.expiryDate}
                                required
                            />
                        </Grid>

                        <Grid item xs={12}>
                            <ImageUploadField
                                field="imagePath"
                                value={formData.imagePath}
                                onUpload={handleImageUpload}
                                onDelete={handleImageDelete}
                                label="Image Thumbnail"
                                classes={classes}
                            />
                        </Grid>

                        <Grid item xs={12} md={6}>
                            <Typography variant="body2" className={classes.sectionText}>
                                Status
                            </Typography>
                            <ToggleButtonGroup
                                value={formData.status ? "ACTIVE" : "INACTIVE"}
                                onChange={(event, newValue) => {
                                    if (newValue !== null) {
                                        setFormData(prev => ({
                                            ...prev,
                                            status: newValue === "ACTIVE"
                                        }));
                                    }
                                }}
                                exclusive
                                aria-label="Status"
                                className={classes.statusToggle}
                            >
                                <ToggleButton value="ACTIVE" aria-label="Active" style={{ width: "50%" }}>
                                    ACTIVE
                                </ToggleButton>
                                <ToggleButton value="INACTIVE" aria-label="Inactive" style={{ width: "50%" }}>
                                    INACTIVE
                                </ToggleButton>
                            </ToggleButtonGroup>
                        </Grid>
                    </Grid>
                </Box>

                {/* Upload Section - Only show in edit mode */}
                {isEditMode && (
                    <Paper className={classes.uploadSection}>
                        <Typography variant="h6" className={classes.sectionTitle}>
                            Import Coupons
                        </Typography>
                        <Typography variant="body2" color="textSecondary" className={classes.uploadDescription}>
                            Upload an Excel file (.xls, .xlsx) or CSV file to import multiple coupons for this template.
                            {" "}
                            <a href="/sample/promocode-sample.xlsx" target="_blank" rel="noopener noreferrer">
                                Download sample file
                            </a>
                        </Typography>
                        <Box className={classes.uploadButtonContainer}>
                            <Button
                                variant="outlined"
                                component="label"
                                startIcon={<CloudUploadIcon />}
                                disabled={isUploading}
                            >
                                {isUploading ? 'Uploading...' : 'Upload Excel File'}
                                <input
                                    id="coupon-upload-input"
                                    type="file"
                                    accept=".xls,.xlsx,.csv"
                                    onChange={handleFileSelect}
                                    style={{ display: 'none' }}
                                />
                            </Button>
                        </Box>
                    </Paper>
                )}

                <Box className={classes.buttonGroup}>
                    <Button
                        variant="contained"
                        color="primary"
                        startIcon={<SaveIcon />}
                        onClick={handleSubmit}
                    >
                        {isEditMode ? "Update" : "Create"}
                    </Button>
                    <Button
                        variant="outlined"
                        onClick={handleCancel}
                    >
                        Cancel
                    </Button>
                </Box>
            </Paper>
        </div>
    );
};

export default CouponEditingPage;
